import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { getCandidato } from "@/lib/api";
import { useComparison } from "@/contexts/ComparisonContext";
import { isAuthenticated } from "@/lib/api";
import { useSettings } from "@/contexts/SettingsContext";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, Loader2, Trash2, ExternalLink, LayoutGrid, FileText, MapPin, Mail, Phone, Briefcase, GraduationCap, Award, BookOpen, Clock, GripVertical } from "lucide-react";

const getSituacionLabel = (s) => {
  if (s === "freelance") return "Freelance";
  if (s === "asalariado") return "Asalariado/a";
  if (s === "estudiante") return "Estudiante";
  return "En paro";
};


const getScoreColors = (score) => {
  if (score >= 75) return "bg-emerald-100 text-emerald-700";
  if (score >= 50) return "bg-amber-100 text-amber-700";
  if (score >= 25) return "bg-orange-100 text-orange-700";
  return "bg-red-100 text-red-700";
};

const parseRangoK = (str) => {
  if (!str || typeof str !== "string") return { minK: null, maxK: null };
  const cleaned = str.trim().replace(/k|€/gi, "");
  const parts = cleaned.split(/[-–—\s]+/).map((p) => p.trim().replace(/,/g, "."));
  const nums = parts.map((p) => parseFloat(p, 10)).filter((n) => !Number.isNaN(n) && n > 0);
  if (nums.length === 0) return { minK: null, maxK: null };
  const minRaw = Math.min(...nums);
  const maxRaw = Math.max(...nums);
  const toK = (n) => (n >= 1000 ? n / 1000 : n);
  return { minK: toK(minRaw), maxK: toK(maxRaw) };
};
const formatRangoK = (k) => (k % 1 === 0 ? `${k}` : k.toFixed(1));

const getSalaryScoreAdjustment = (rangoStr) => {
  const { maxK } = parseRangoK(rangoStr);
  if (maxK == null) return 0;
  if (maxK <= 20) return 5;
  if (maxK <= 30) return 0;
  return -5;
};

const getRangoChipColors = (rangoStr) => {
  const { maxK } = parseRangoK(rangoStr);
  if (maxK == null) return "bg-muted text-muted-foreground";
  if (maxK <= 20) return "bg-emerald-100 text-emerald-700";
  if (maxK <= 30) return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

const getSituacionChipColors = (situacion) => {
  if (!situacion) return "bg-muted text-muted-foreground";
  switch (situacion) {
    case "freelance": return "bg-amber-100 text-amber-800";
    case "asalariado": return "bg-violet-100 text-violet-800";
    case "estudiante": return "bg-teal-100 text-teal-800";
    default: return "bg-slate-200 text-slate-700";
  }
};

const displayRangoLabel = (rangoStr) => {
  const { minK, maxK } = parseRangoK(rangoStr);
  if (minK == null && maxK == null) return rangoStr || "";
  if (minK != null && maxK != null && minK !== maxK) return `${formatRangoK(minK)}k - ${formatRangoK(maxK)}k`;
  if (maxK != null) return `${formatRangoK(maxK)}k`;
  return rangoStr || "";
};

const TRUNCATE_LEN = 200;

function Cell({ children, className = "" }) {
  return (
    <td className={`p-3 align-top border-b border-border text-sm ${className}`}>
      {children != null && children !== "" ? children : "—"}
    </td>
  );
}

function ExpandableCell({ sectionLabel, value, className = "" }) {
  const str = value != null && value !== "" ? String(value) : "";
  const isLong = str.length > TRUNCATE_LEN;
  const display = isLong ? str.slice(0, TRUNCATE_LEN) + "…" : str;
  return (
    <td className={`p-3 align-top border-b border-border text-sm ${className}`}>
      {display || "—"}
      {isLong && (
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="link" className="h-auto px-1.5 py-0.5 ml-1 text-xs font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline underline-offset-2 rounded bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-900/40">
              Ver más
            </Button>
          </PopoverTrigger>
          <PopoverContent className="max-w-md max-h-[70vh] overflow-y-auto" align="start">
            <p className="text-xs font-medium text-muted-foreground mb-2">{sectionLabel}</p>
            <p className="text-sm whitespace-pre-wrap break-words">{str}</p>
          </PopoverContent>
        </Popover>
      )}
    </td>
  );
}

export default function CompararPage() {
  const navigate = useNavigate();
  const { comparisonIds, removeFromComparison, clearComparison } = useComparison();
  const [candidatos, setCandidatos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [soloClave, setSoloClave] = useState(false);
  const [sheetCandidato, setSheetCandidato] = useState(null);
  const [columnOrder, setColumnOrder] = useState([]);
  const [dragOverId, setDragOverId] = useState(null);
  const { showScore, maskValue } = useSettings();

  const orderedCandidatos = useMemo(() => {
    if (columnOrder.length === 0) return candidatos;
    const byId = new Map(candidatos.map((c) => [c.id, c]));
    return columnOrder.map((id) => byId.get(id)).filter(Boolean);
  }, [candidatos, columnOrder]);

  useEffect(() => {
    if (candidatos.length === 0) return;
    const ids = candidatos.map((c) => c.id);
    setColumnOrder((prev) => {
      if (prev.length === 0) return ids;
      const prevSet = new Set(prev);
      const kept = prev.filter((id) => ids.includes(id));
      const added = ids.filter((id) => !prevSet.has(id));
      return [...kept, ...added];
    });
  }, [candidatos]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  useEffect(() => {
    const ids = comparisonIds.length > 0 ? comparisonIds : (() => {
      try {
        const raw = sessionStorage.getItem("comparar_ids");
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed : [];
      } catch (_) {
        return [];
      }
    })();

    if (ids.length === 0) {
      navigate("/candidatos");
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all(ids.map((id) => getCandidato(id)))
      .then((data) => {
        if (!cancelled) setCandidatos(data.filter(Boolean));
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "Error al cargar candidatos");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [comparisonIds, navigate]);

  const handleClear = () => {
    clearComparison();
    navigate("/candidatos");
  };

  const handleColumnDragStart = (e, candidatoId) => {
    e.dataTransfer.setData("text/plain", candidatoId);
    e.dataTransfer.effectAllowed = "move";
    e.currentTarget.setAttribute("data-dragging", "true");
  };

  const handleColumnDragEnd = (e) => {
    e.currentTarget.removeAttribute("data-dragging");
    setDragOverId(null);
  };

  const handleColumnDragOver = (e, targetId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverId(targetId);
  };

  const handleColumnDragLeave = () => {
    setDragOverId(null);
  };

  const handleColumnDrop = (e, targetId) => {
    e.preventDefault();
    const draggedId = e.dataTransfer.getData("text/plain");
    if (!draggedId || draggedId === targetId) return;
    setColumnOrder((prev) => {
      const fromIdx = prev.indexOf(draggedId);
      const toIdx = prev.indexOf(targetId);
      if (fromIdx === -1 || toIdx === -1) return prev;
      const next = [...prev];
      next.splice(fromIdx, 1);
      next.splice(toIdx, 0, draggedId);
      return next;
    });
    setDragOverId(null);
  };

  const sectionsAll = [
    { key: "rol", label: "Rol principal", clave: true, get: (c) => c.rol_principal || "" },
    { key: "nivel", label: "Nivel", clave: true, get: (c) => c.nivel || "" },
    { key: "exp", label: "Años de experiencia", clave: true, get: (c) => (c.años_experiencia != null ? `${c.años_experiencia}` : "") },
    { key: "situacion", label: "Situación actual", clave: true, get: (c) => getSituacionLabel(c.situacion_actual || "ninguno") },
    { key: "rango", label: "Rango salarial", clave: true, get: (c) => displayRangoLabel(c.rango_salarial) },
    { key: "ubicacion", label: "Ubicación", clave: true, get: (c) => [c.ciudad, c.provincia].filter(Boolean).join(", ") },
    { key: "segmentos", label: "Segmentos", clave: true, get: (c) => Array.isArray(c.segmentos) ? c.segmentos.join(", ") : "" },
    { key: "habilidades", label: "Habilidades", clave: true, get: (c) => Array.isArray(c.habilidades) ? c.habilidades.join(", ") : "" },
    { key: "estudios", label: "Estudios", clave: true, get: (c) => {
      const e = c.estudios;
      if (!Array.isArray(e) || e.length === 0) return "";
      return e.map((x) => [x.titulo, x.centro].filter(Boolean).join(" — ")).join(" · ");
    }},
    { key: "cursos", label: "Cursos / certificaciones", clave: false, get: (c) => Array.isArray(c.cursos) ? c.cursos.join(", ") : "" },
    { key: "idiomas", label: "Idiomas", clave: false, get: (c) => Array.isArray(c.idiomas) ? c.idiomas.join(", ") : "" },
    { key: "experiencia", label: "Experiencia laboral", clave: true, get: (c) => {
      const exp = c.experiencias_laborales;
      if (!Array.isArray(exp) || exp.length === 0) return "";
      return exp.slice(0, 5).map((x) => `${x.cargo || ""} en ${x.empresa || ""} (${x.años_duracion ?? "?"} años)`).join(" · ");
    }},
    { key: "resumen", label: "Resumen perfil", clave: true, get: (c) => c.resumen_perfil || "" },
    { key: "contactado", label: "Contactado", clave: false, get: (c) => c.contactado ? "Sí" : "No" },
  ];

  const sections = soloClave ? sectionsAll.filter((s) => s.clave) : sectionsAll;

  if (!isAuthenticated()) return null;
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center gap-4 p-6">
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" onClick={() => navigate("/candidatos")}>
          Volver a candidatos
        </Button>
      </div>
    );
  }
  if (candidatos.length === 0) {
    navigate("/candidatos");
    return null;
  }

  return (
    <div className="min-h-dvh bg-background">
      <header className="sticky top-0 z-20 border-b bg-background/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-6 py-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/candidatos")} className="gap-2">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              Volver a candidatos
            </Button>
            <h1 className="text-lg font-semibold flex items-center gap-2">
              <LayoutGrid className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
              Comparativa de perfiles
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={soloClave ? "secondary" : "outline"}
              size="sm"
              onClick={() => setSoloClave(true)}
            >
              Secciones clave
            </Button>
            <Button
              variant={!soloClave ? "secondary" : "outline"}
              size="sm"
              onClick={() => setSoloClave(false)}
            >
              Todas las secciones
            </Button>
            <Button variant="outline" size="sm" onClick={handleClear} className="gap-1.5 text-muted-foreground">
              <Trash2 className="w-4 h-4" strokeWidth={1.5} />
              Limpiar comparativa
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 overflow-x-auto">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr className="border-b border-border">
              <th className="w-[200px] min-w-[180px] sticky left-0 z-[1] bg-muted/50 p-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider border-b border-r border-border">
                Sección
              </th>
              {orderedCandidatos.map((c) => (
                <th
                  key={c.id}
                  onDragOver={(e) => handleColumnDragOver(e, c.id)}
                  onDragLeave={handleColumnDragLeave}
                  onDrop={(e) => handleColumnDrop(e, c.id)}
                  className={`min-w-[220px] max-w-[280px] p-3 align-top border-b border-border bg-card transition-colors ${dragOverId === c.id ? "bg-primary/10 ring-2 ring-primary/30 ring-inset" : ""}`}
                >
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <span
                          draggable
                          onDragStart={(e) => handleColumnDragStart(e, c.id)}
                          onDragEnd={handleColumnDragEnd}
                          className="cursor-grab active:cursor-grabbing touch-none p-0.5 rounded hover:bg-muted/80 text-muted-foreground hover:text-foreground [&[data-dragging=true]]:opacity-50"
                          title="Arrastra para reordenar la columna"
                          aria-label="Reordenar columna"
                        >
                          <GripVertical className="w-4 h-4" strokeWidth={1.5} />
                        </span>
                        <button
                          type="button"
                          onClick={() => setSheetCandidato(c)}
                          className="text-left font-medium text-sm hover:underline min-w-0 truncate"
                        >
                          {maskValue(c.nombre ?? "", "nombre") || "—"}
                        </button>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          onClick={() => setSheetCandidato(c)}
                          title="Ver ficha"
                        >
                          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => removeFromComparison(c.id)}
                          title="Quitar de comparativa"
                        >
                          <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                        </Button>
                      </div>
                    </div>
                    {showScore && c.score_agencia != null && (
                      <span className={`inline-flex text-xs font-semibold px-2 py-1 rounded w-fit ${getScoreColors((c.score_agencia ?? 0) + getSalaryScoreAdjustment(c.rango_salarial))}`}>
                        {(c.score_agencia ?? 0) + getSalaryScoreAdjustment(c.rango_salarial)}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sections.map((sec) => (
              <tr key={sec.key} className="hover:bg-muted/20 transition-colors">
                <th className="sticky left-0 z-[1] bg-background p-3 text-left text-xs font-medium text-muted-foreground border-b border-r border-border">
                  {sec.label}
                </th>
                {orderedCandidatos.map((c) => {
                  const raw = sec.get(c);
                  const str = raw != null && raw !== "" ? String(raw) : "";
                  if (sec.key === "situacion") {
                    const situacion = c.situacion_actual || "ninguno";
                    return (
                      <Cell key={c.id}>
                        <span className={`inline-block text-sm font-medium px-2.5 py-1 rounded ${getSituacionChipColors(situacion)}`}>
                          {getSituacionLabel(situacion)}
                        </span>
                      </Cell>
                    );
                  }
                  if (sec.key === "rango") {
                    const rangoStr = c.rango_salarial;
                    if (!rangoStr) return <Cell key={c.id}>—</Cell>;
                    return (
                      <Cell key={c.id}>
                        <span className={`inline-block text-sm font-medium px-2.5 py-1 rounded ${getRangoChipColors(rangoStr)}`}>
                          {displayRangoLabel(rangoStr)}
                        </span>
                      </Cell>
                    );
                  }
                  if (sec.key === "contactado") {
                    return (
                      <Cell key={c.id}>
                        {c.contactado ? (
                          <span className="inline-block text-sm font-medium px-2.5 py-1 rounded bg-blue-100 text-blue-700">
                            Sí
                          </span>
                        ) : (
                          <span className="text-sm text-muted-foreground">No</span>
                        )}
                      </Cell>
                    );
                  }
                  if (str.length > TRUNCATE_LEN) {
                    return (
                      <ExpandableCell key={c.id} sectionLabel={sec.label} value={str} />
                    );
                  }
                  return (
                    <Cell key={c.id}>{raw ?? "—"}</Cell>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </main>

      <Sheet open={!!sheetCandidato} onOpenChange={(open) => !open && setSheetCandidato(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0 flex flex-col">
          {sheetCandidato && (() => {
            const c = sheetCandidato;
            const scoreConRango = (c.score_agencia ?? 0) + getSalaryScoreAdjustment(c.rango_salarial);
            return (
              <>
                <SheetHeader className="p-6 pb-4 border-b shrink-0">
                  <div className="flex items-start justify-between gap-4 pr-8">
                    <div>
                      <SheetTitle className="text-left text-xl">
                        {maskValue(c.nombre ?? "", "nombre") || "—"}
                      </SheetTitle>
                      <p className="text-muted-foreground text-sm mt-1">
                        {c.rol_principal || "Sin rol definido"}
                      </p>
                    </div>
                    {showScore && c.score_agencia != null && (
                      <span className={`inline-flex items-center justify-center text-lg font-semibold px-3 py-1.5 rounded shrink-0 ${getScoreColors(scoreConRango)}`}>
                        {scoreConRango}
                      </span>
                    )}
                  </div>
                </SheetHeader>
                <div className="flex-1 overflow-y-auto p-6 pt-4 space-y-6">
                  <div className="flex items-center gap-3 flex-wrap">
                    {c.nivel && (
                      <Badge variant="outline" className="text-sm font-normal">{c.nivel}</Badge>
                    )}
                    {c.años_experiencia > 0 && (
                      <span className="text-sm text-muted-foreground flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {c.años_experiencia} años de experiencia
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-block text-sm font-medium px-2.5 py-1 rounded ${getSituacionChipColors(c.situacion_actual || "ninguno")}`}>
                      {getSituacionLabel(c.situacion_actual || "ninguno")}
                    </span>
                    {c.contactado && (
                      <span className="inline-block text-sm font-medium px-2.5 py-1 rounded bg-blue-100 text-blue-700">
                        Contactado
                      </span>
                    )}
                    {c.rango_salarial && (
                      <span className={`inline-block text-sm font-medium px-2.5 py-1 rounded ${getRangoChipColors(c.rango_salarial)}`}>
                        {displayRangoLabel(c.rango_salarial)}
                      </span>
                    )}
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-sm font-medium mb-4">Contacto y ubicación</h2>
                    <div className="space-y-3">
                      {c.email && (
                        <div className="flex items-center gap-3 text-sm">
                          <Mail className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                          <a href={`mailto:${c.email}`} className="hover:underline break-all">{c.email}</a>
                        </div>
                      )}
                      {c.telefono && (
                        <div className="flex items-center gap-3 text-sm">
                          <Phone className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                          <a href={`tel:${c.telefono}`} className="hover:underline">{c.telefono}</a>
                        </div>
                      )}
                      {(c.ciudad || c.provincia) && (
                        <div className="flex items-center gap-3 text-sm">
                          <MapPin className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                          <span>{[c.ciudad, c.provincia].filter(Boolean).join(", ")}</span>
                        </div>
                      )}
                      {!c.email && !c.telefono && !c.ciudad && !c.provincia && (
                        <p className="text-sm text-muted-foreground">Sin información de contacto</p>
                      )}
                    </div>
                  </div>

                  <Separator />

                  <div>
                    <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                      Trayectoria profesional
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {c.resumen_perfil || "Sin información de trayectoria"}
                    </p>
                  </div>

                  {Array.isArray(c.experiencias_laborales) && c.experiencias_laborales.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                          <Clock className="w-4 h-4" strokeWidth={1.5} />
                          Experiencia laboral
                        </h2>
                        <div className="space-y-3 text-sm">
                          {c.experiencias_laborales.slice(0, 8).map((exp, i) => (
                            <div key={i}>
                              <p className="font-medium">{exp.cargo || "Sin especificar"}</p>
                              {[exp.empresa, exp.periodo].filter(Boolean).length > 0 && (
                                <p className="text-muted-foreground text-xs">
                                  {[exp.empresa, exp.periodo].filter(Boolean).join(" · ")}
                                </p>
                              )}
                              {exp.años_duracion != null && (
                                <p className="text-muted-foreground text-xs">{exp.años_duracion} años</p>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  <div>
                    <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                      <GraduationCap className="w-4 h-4" strokeWidth={1.5} />
                      Formación académica
                    </h2>
                    {Array.isArray(c.estudios) && c.estudios.length > 0 ? (
                      <div className="space-y-3 text-sm">
                        {c.estudios.map((estudio, i) => (
                          <div key={i}>
                            <p className="font-medium">{estudio.titulo}</p>
                            <p className="text-muted-foreground text-xs">
                              {estudio.centro} {estudio.año && `· ${estudio.año}`}
                            </p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Sin estudios registrados</p>
                    )}
                  </div>

                  {Array.isArray(c.segmentos) && c.segmentos.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                          <Briefcase className="w-4 h-4" strokeWidth={1.5} />
                          Segmentos
                        </h2>
                        <div className="flex flex-wrap gap-2">
                          {c.segmentos.map((seg, i) => (
                            <Badge key={i} variant="secondary" className="text-sm font-normal">
                              {seg}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {Array.isArray(c.habilidades) && c.habilidades.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                          <Award className="w-4 h-4" strokeWidth={1.5} />
                          Habilidades
                        </h2>
                        <div className="flex flex-wrap gap-2">
                          {c.habilidades.map((hab, i) => (
                            <Badge key={i} variant="outline" className="text-sm font-normal">
                              {hab}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  {Array.isArray(c.cursos) && c.cursos.length > 0 && (
                    <>
                      <Separator />
                      <div>
                        <h2 className="text-sm font-medium mb-4 flex items-center gap-2">
                          <BookOpen className="w-4 h-4" strokeWidth={1.5} />
                          Cursos y certificaciones
                        </h2>
                        <div className="flex flex-wrap gap-2">
                          {c.cursos.map((curso, i) => (
                            <Badge key={i} variant="secondary" className="text-sm font-normal">
                              {curso}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <Separator />

                  <Button
                    variant="outline"
                    className="w-full gap-2"
                    onClick={() => {
                      setSheetCandidato(null);
                      navigate(`/candidatos/${c.id}`);
                    }}
                  >
                    <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                    Abrir ficha completa
                  </Button>
                </div>
              </>
            );
          })()}
        </SheetContent>
      </Sheet>
    </div>
  );
}
